import { useCallback, useRef } from "react";
import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";
import { createContainer } from "unstated-next";

/**
 * Maintains a cache of LOS frontline API results.
 * Avoids the need to re-fetch when unmounting downline list components.
 */
export default function useDownlineCache() {
  const { makeAuthedRequest } = Auth.useContainer();
  const { logError } = useLogger({ prefix: "useDownlineCache" });

  const cache = useRef({});

  /**
   * Retrieves list of frontline IBOs. Optionally with filters.
   *
   * Filters options are:
   * - "only-pins"
   * - "active" / "!active"
   * - "validated" / "validated"
   * - ""
   */
  return useCallback(async (iboNumber, filters = []) => {
    const cached = cache.current[iboNumber];

    // Return cached if entry exists and filters are the same.
    if (cached) {
      if (cached.filters === JSON.stringify(filters)) {
        return cached.results;
      }
    }

    return makeAuthedRequest(`/v1/membership/los/frontline/${iboNumber}`)
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
      .then((results) => {
        results.sort(downlineSort);

        cache.current[iboNumber] = {
          results,
          filters: JSON.stringify(filters),
        };

        return results;
      })
      .catch(logError);
  }, []);
}

export const DownlineCache = createContainer(useDownlineCache);

/**
 * Sort descending by pin level, alphabetically by membership status, then alphabetically by name.
 */
function downlineSort(a, b) {
  let score = 0;

  if (a.pinLevel > b.pinLevel) score -= 100;
  if (a.pinLevel < b.pinLevel) score += 100;

  const statusA = (a.status ?? "z").toLowerCase();
  const statusB = (a.status ?? "z").toLowerCase();

  if (statusA < statusB) score -= 10;
  if (statusA > statusB) score += 10;

  if (a.lastName < b.lastName) score -= 1;
  if (a.lastName > b.lastName) score += 1;

  return score;
}
